import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Grid } from "@mui/material";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ThumbnailSkeleton } from "components/common/ThumbnailSkeleton";
import { Typography } from "new-components/typography/Typography";
import { ListResponse } from "types/api";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { getColorMap, getTitleSuffix } from "utils/imageQueriesUtils";
import { redirectToDataPage } from "utils/redirectDataPage";
import { isExplorePage } from "utils/utilityFunctions";

import { ImageQueryActions, ImageQueryActionsRef } from "./ImageQueryActions";
import { ClickOrigin, PageContext } from "./ImageQueryPreview";
import { ImageQueryView } from "./ImageQueryView";

interface ImageQueryGridProps {
  title: string;
  status: JSX.Element | null;
  imageQueries: ListResponse<ImageQuery> | undefined;
  onRefetch: () => Promise<void>;
  detector: Detector | undefined;
  pageContext?: PageContext;
  answerValue?: string;
  columns?: number;
}

export const ImageQueryGrid = React.memo((props: ImageQueryGridProps) => {
  const {
    title,
    status,
    imageQueries,
    onRefetch,
    detector,
    answerValue,
    pageContext = PageContext.DetectorDetails,
    columns = 3,
  } = props;

  const titleColor = getColorMap(answerValue || title.toUpperCase());
  const [showMoreImages, setShowMoreImages] = useState<boolean>(false);
  const actionsRef = useRef<ImageQueryActionsRef>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | HTMLDivElement | null>(null);

  const getMaxImages = (showMoreImages: boolean, pageContext: PageContext) => {
    if (isExplorePage(pageContext)) {
      return 12;
    }
    return showMoreImages ? columns * 3 : columns;
  };

  const maxImages = getMaxImages(showMoreImages, pageContext);

  const isInitiallyVisible = (index: number) => index < 3;

  const onInQueueImageClick = (currentIndex: number, clickOrigin: ClickOrigin, event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget as HTMLButtonElement | HTMLDivElement);
    actionsRef.current?.onInQueueImageClick(currentIndex, clickOrigin);
  };

  const getGridItemProps = (index: number) => {
    const visible = index < maxImages;
    return {
      sx: {
        transition: "all 0.3s ease-in-out",
        overflow: "hidden",
        height: visible ? "auto" : 0,
        opacity: isInitiallyVisible(index) || visible ? 1 : 0,
        mb: { xs: 3, sm: 0 },
      },
      "data-testid": "image-query-grid-item",
    };
  };

  const columnSize = 12 / columns;
  const areColumnsOdd = columns % 2 !== 0;
  const middleColumn = Math.floor(columns / 2);

  return (
    status || (
      <Box className="border-b border-gray-400 border-solid">
        {pageContext !== PageContext.Explore && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              my: 1,
            }}
          >
            <Typography fontWeight="semibold" data-testid="image-query-grid-title">
              <strong style={{ color: titleColor }} className="capitalize">
                {title}
              </strong>{" "}
              Answer Examples:
            </Typography>
            {imageQueries?.results && imageQueries?.results.length > 0 && (
              <Box
                onClick={() => setShowMoreImages(!showMoreImages)}
                className="cursor-pointer flex items-center bg-inherit"
                component="button"
              >
                <Typography fontWeight="semibold" className="text-neutral-400">
                  {showMoreImages ? "Show less" : "Show more"}
                </Typography>
                <span>
                  <KeyboardArrowDownIcon
                    className={`mt-[6px] transform transition-transform duration-500 ${showMoreImages ? "-rotate-180" : ""
                      } text-neutral-400 mt-[3px] `}
                  />
                </span>
              </Box>
            )}
          </Box>
        )}
        <Grid
          container
          columnSpacing={{ xs: 1, md: 2 }}
          rowSpacing={{ xs: 1, md: 2 }}
          sx={{
            marginBottom: detector?.mode === "TEXT" ? "1rem" : "0",
          }}
        >
          {imageQueries?.results.length && imageQueries?.results ? (
            <>
              {imageQueries.results.map((posicheck: ImageQuery, index: number) => {
                const gridItemProps = getGridItemProps(index);
                return (
                  <Grid item xs={12} sm={columnSize} md={columnSize} key={posicheck.iq_id} {...gridItemProps}>
                    <Box
                      sx={{ height: "221px" }}
                      key={posicheck.iq_id}
                    >
                      <ImageQueryView
                        id={`${title}-image-query-${index}`}
                        imageQuery={posicheck}
                        openFocusModal={(clickOrigin, event) => onInQueueImageClick(index, clickOrigin, event)}
                        pageContext={pageContext}
                        titleSuffix={getTitleSuffix(posicheck, detector?.mode, pageContext)}
                        showConfidence={detector?.mode === DetectorModes.BINARY}
                        showTimestamp
                      />
                    </Box>
                  </Grid>
                );
              })}
            </>
          ) : (
            [...Array(columns)].map((_, index) => (
              <Grid
                item
                xs={12}
                sm={columnSize}
                md={columnSize}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                sx={{ height: "200px" }}
              >
                <ThumbnailSkeleton title={title} showText={areColumnsOdd ? index === middleColumn : index === 0} />
              </Grid>
            ))
          )}
        </Grid>
        {detector && detector.mode !== "TEXT" && (
          <Box className="flex justify-center">
            <Link
              to={redirectToDataPage({
                detectorId: detector.id,
                ...(isExplorePage(pageContext)
                  ? undefined
                  : {
                    bestAnswer:
                      answerValue || (detector?.mode === DetectorModes.COUNT ? title.replaceAll(" ", "") : title),
                  }),
              })}
              className="my-2 cursor-pointer flex items-center gap-1"
            >
              See all <span className="font-semibold capitalize">{title}</span>
              image queries{" "}
              <span className="mt-1">
                <KeyboardArrowRightIcon />
              </span>
            </Link>
          </Box>
        )}
        <ImageQueryActions
          ref={actionsRef}
          detector={detector}
          imageQueries={imageQueries}
          onRefetch={onRefetch}
          pageContext={pageContext}
          anchorEl={anchorEl}
        />
      </Box>
    )
  );
});
